import { useChatStore } from '../stores/chatStore';
import { useAuthStore, getAuthHeaders } from '../stores/authStore';
import { performSilentRefresh } from './authenticatedFetch';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface StreamToken {
  messageId: string;
  delta: string;
  sequence: number;
  timestamp: string;
}

export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  sentAt?: string;
  deliveredAt?: string;
  error?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'failed';
}

export interface WebSocketCallbacks {
  onConnectionState?: (state: ConnectionState) => void;
  onToken?: (token: StreamToken) => void;
  onMessage?: (message: Message) => void;
  onError?: (error: Error) => void;
  onTyping?: (conversationId: string, isTyping: boolean) => void;
}

// Gateway protocol message types
interface GatewayEvent {
  type: 'event';
  event: string;
  payload?: any;
}

interface GatewayRequest {
  type: 'req';
  id: string;
  method: string;
  params?: any;
}

interface GatewayResponse {
  type: 'res';
  id: string;
  ok: boolean;
  payload?: any;
  error?: string;
}

type GatewayMessage = GatewayEvent | GatewayRequest | GatewayResponse;

class AllyGatewayClient {
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 1000;
  private callbacks: WebSocketCallbacks = {};
  private wsUrl: string;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  
  // Gateway protocol state
  private pending = new Map<string, { resolve: (value?: unknown) => void, reject: (reason?: unknown) => void }>();
  private isAuthenticated = false;
  private mainSessionKey = 'agent:main:main';
  
  constructor() {
    this.wsUrl = this.getWebSocketUrl();
  }

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/ally`;
  }

  async connect(_agentId: string = 'main', token?: string): Promise<void> {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      return;
    }

    const accessToken = token || useAuthStore.getState().token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    this.wsUrl = this.getWebSocketUrl();
    this.setConnectionState('connecting');

    return new Promise((resolve, reject) => {
      let settled = false;
      let checkAuth: ReturnType<typeof setInterval> | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        if (checkAuth) {
          clearInterval(checkAuth);
          checkAuth = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const resolveOnce = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      const rejectOnce = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      try {
        this.ws = new WebSocket(this.wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket opened, sending auth handshake...');
          this.ws?.send(JSON.stringify({ type: 'auth', token: accessToken }));
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed', event.code, event.reason);
          this.stopHeartbeat();
          this.isAuthenticated = false;
          this.setConnectionState('disconnected');

          if (event.code !== 1000 && !settled) {
            rejectOnce(new Error(event.reason || 'WebSocket closed before authentication'));
          }
          
          if (event.code !== 1000) {
            // Abnormal close, attempt to reconnect
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.callbacks.onError?.(new Error('WebSocket connection failed'));
          this.setConnectionState('disconnected');
          rejectOnce(new Error('WebSocket connection failed'));
        };

        checkAuth = setInterval(() => {
          if (this.isAuthenticated) {
            resolveOnce();
          }
        }, 100);

        timeoutId = setTimeout(() => {
          if (!this.isAuthenticated) {
            this.ws?.close();
            rejectOnce(new Error('WebSocket connection timeout'));
          }
        }, 10000);
      } catch (error) {
        cleanup();
        this.setConnectionState('disconnected');
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.isAuthenticated = false;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }
    this.setConnectionState('disconnected');
  }

  private handleMessage(raw: string): void {
    try {
      const msg: GatewayMessage = JSON.parse(raw);
      
      if (msg.type === 'event') {
        // Handle connect.challenge first
        if (msg.event === 'connect.challenge') {
          console.log('Received connect.challenge, sending connect...');
          this.sendConnect();
          return;
        }
        // Handle chat events
        this.handleChatEvent(msg);
        return;
      }
      
      if (msg.type === 'res') {
        const pending = this.pending.get(msg.id);
        if (pending) {
          this.pending.delete(msg.id);
          if (msg.ok) {
            // Special handling for connect response
            if (msg.id === '__connect' && !this.isAuthenticated) {
              this.isAuthenticated = true;
              this.setConnectionState('connected');
              this.reconnectAttempts = 0;
              this.startHeartbeat();
              console.log('Gateway auth successful');

              const sessionDefaults = msg.payload?.snapshot?.sessionDefaults;
              if (sessionDefaults?.mainSessionKey) {
                this.mainSessionKey = sessionDefaults.mainSessionKey;
              }

              const health = msg.payload?.snapshot?.health;
              const defaultAgentId = health?.defaultAgentId || sessionDefaults?.defaultAgentId;
              void this.loadConfiguredAgents(defaultAgentId);
            }
            pending.resolve(msg.payload);
          } else {
            pending.reject(new Error(msg.error || 'Request failed'));
          }
        }
      }
    } catch (error) {
      console.error('Failed to parse gateway message:', error);
      this.callbacks.onError?.(new Error('Invalid message format'));
    }
  }

  private handleChatEvent(event: GatewayEvent): void {
    switch (event.event) {
      case 'chat.delta':
        this.handleStreamDelta(event.payload);
        break;
      case 'chat.final':
        this.handleStreamFinal(event.payload);
        break;
      case 'chat.error':
        this.callbacks.onError?.(new Error(event.payload?.message || 'Chat error'));
        break;
      default:
        console.log('Unhandled chat event:', event.event, event.payload);
    }
  }

  private handleStreamDelta(payload: any): void {
    if (payload?.content) {
      // Create a StreamToken from the delta
      const token: StreamToken = {
        messageId: payload.messageId || 'streaming',
        delta: payload.content,
        sequence: payload.sequence || 0,
        timestamp: new Date().toISOString()
      };
      
      // Update chat store with streaming token
      const chatStore = useChatStore.getState();
      chatStore.addToken(token.messageId, token.delta);
      this.callbacks.onToken?.(token);
    }
  }

  private handleStreamFinal(payload: any): void {
    if (payload?.content) {
      // Complete the message in chat store
      const message: Message = {
        id: payload.messageId || Date.now().toString(),
        conversationId: payload.conversationId || 'default',
        role: 'assistant',
        content: payload.content,
        timestamp: new Date().toISOString()
      };
      
      const chatStore = useChatStore.getState();
      chatStore.completeMessage(message.id, message.conversationId, message.content);
      this.callbacks.onMessage?.(message);
    }
  }

  private sendConnect(): void {
    this.request('connect', {
      minProtocol: 4,
      maxProtocol: 4,
      client: {
        id: 'webchat-ui',
        version: '2.0.0',
        platform: 'web',
        mode: 'webchat'
      },
      role: 'operator',
      scopes: ['operator.read', 'operator.write'],
      caps: ['tool-events']
    }, '__connect').catch((error) => {
      console.error('Gateway auth failed:', error);
      this.callbacks.onError?.(error instanceof Error ? error : new Error('Gateway auth failed'));
      this.ws?.close();
    });
  }

  private request(method: string, params: any, id?: string): Promise<any> {
    const requestId = id || crypto.randomUUID();
    
    return new Promise((resolve, reject) => {
      if (!this.pending.has(requestId)) {
        this.pending.set(requestId, { resolve, reject });
      }
      
      const message: GatewayRequest = {
        type: 'req',
        id: requestId,
        method,
        params
      };
      
      this.ws?.send(JSON.stringify(message));
    });
  }

  private async loadConfiguredAgents(defaultAgentId?: string): Promise<void> {
    try {
      const response = await fetch('/api/config', {
        headers: getAuthHeaders({ includeImpersonation: true }),
      });
      if (!response.ok) {
        throw new Error('Failed to load agents from config');
      }

      const config = await response.json();
      const configAgents = Array.isArray(config?.agents) ? config.agents : [];

      const { useChatStore } = await import('../stores/chatStore');
      if (configAgents.length > 0) {
        useChatStore.getState().setAgents(configAgents);
      }

      if (defaultAgentId || configAgents[0]?.id) {
        useChatStore.getState().ensureDefaultConversation(defaultAgentId || configAgents[0].id);
      }
    } catch (error) {
      console.error('Failed to load agents from config', error);
    }
  }

  sendMessage(sessionKey: string, content: string): Promise<void> {
    if (!this.isConnected() || !this.isAuthenticated) {
      throw new Error('Gateway not authenticated');
    }

    const resolvedSessionKey = this.mainSessionKey || sessionKey;
    
    return this.request('chat.send', {
      sessionKey: resolvedSessionKey,
      message: content
    });
  }

  // Helper method for agent-based chat (alternative to sessionKey)
  sendAgentMessage(agentId: string, content: string): Promise<void> {
    if (!this.isConnected() || !this.isAuthenticated) {
      throw new Error('Gateway not authenticated'); 
    }
    
    return this.request('chat.send', {
      agentId,
      content
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      // D6-16: settle on 'disconnected' so the persistent connection-lost
      // banner offers a manual retry instead of stranding the user silently.
      this.setConnectionState('disconnected');
      this.callbacks.onError?.(new Error('Max reconnection attempts reached'));
      return;
    }

    this.setConnectionState('reconnecting');
    this.reconnectAttempts++;
    
    const delay = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(async () => {
      if (this.connectionState === 'reconnecting') {
        console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`);
        // Refresh the MSAL token + backend session before reconnecting.
        // This is the most common cause of WS auth failure after idle.
        try {
          await performSilentRefresh();
        } catch (refreshError) {
          console.warn('Token refresh before WS reconnect failed:', refreshError);
        }
        this.connect('main').catch(() => {
          this.scheduleReconnect();
        });
      }
    }, delay);
  }

  /**
   * D6-16: user-initiated reconnect from the connection-lost banner.
   * Resets the backoff budget so a manual retry always gets a fresh set of
   * automatic attempts behind it if the first try fails.
   */
  async reconnectNow(): Promise<void> {
    this.reconnectAttempts = 0;
    try {
      await performSilentRefresh();
    } catch (refreshError) {
      console.warn('Token refresh before manual reconnect failed:', refreshError);
    }
    return this.connect('main');
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.callbacks.onConnectionState?.(state);
    
    try {
      useAuthStore.getState().setConnectionState(state);
    } catch {
      // Auth store not available yet
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    // Gateway doesn't need explicit pings - it handles connection health internally
    // But we can implement periodic health checks if needed
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  isConnected(): boolean {
    return this.connectionState === 'connected' && this.isAuthenticated;
  }

  // Event handler registration methods
  onConnectionState(callback: (state: ConnectionState) => void): () => void {
    this.callbacks.onConnectionState = callback;
    return () => { this.callbacks.onConnectionState = undefined; };
  }

  onToken(callback: (token: StreamToken) => void): () => void {
    this.callbacks.onToken = callback;
    return () => { this.callbacks.onToken = undefined; };
  }

  onMessage(callback: (message: Message) => void): () => void {
    this.callbacks.onMessage = callback;
    return () => { this.callbacks.onMessage = undefined; };
  }

  onError(callback: (error: Error) => void): () => void {
    this.callbacks.onError = callback;
    return () => { this.callbacks.onError = undefined; };
  }

  onTyping(callback: (conversationId: string, isTyping: boolean) => void): () => void {
    this.callbacks.onTyping = callback;
    return () => { this.callbacks.onTyping = undefined; };
  }
}

// Legacy compatibility - keep old interface methods
export class AllyWebSocketService extends AllyGatewayClient {
  // Legacy send method
  send(payload: any): Promise<void> {
    // Convert old format to new gateway format
    if (payload.type === 'message') {
      return this.sendMessage(payload.conversationId || 'main', payload.content);
    }
    return Promise.resolve();
  }

  // Legacy sendMessage method - override to maintain compatibility
  sendMessage(conversationId: string, content: string): Promise<void> {
    return super.sendMessage(conversationId, content);
  }
}

// Export singleton instance
export const allyWebSocket = new AllyWebSocketService();
export { AllyGatewayClient };